import { useCallback, useEffect, useMemo, useState } from "react";
import Loading from "../../components/Loading";
import Paginacao from "../../components/Paginacao/Simples";
import { apiLinkImages, merchantCenterAccountId } from "../../config";
import { formatMoney } from "../../utils";
import { Notification, notify } from "../../utils/notification";

const PAGE_SIZE = 50;
const VIEW_STORAGE_KEY = "merchantCenterViewMode";

function loadViewMode() {
	try {
		return localStorage.getItem(VIEW_STORAGE_KEY) === "list" ? "list" : "grid";
	} catch {
		return "grid";
	}
}

function countPending(selectedIds, savedIds) {
	const selected = new Set(selectedIds.map(String));
	let pending = 0;
	savedIds.forEach((id) => {
		if (!selected.has(String(id))) pending++;
	});
	selected.forEach((id) => {
		if (!savedIds.has(String(id))) pending++;
	});
	return pending;
}

function getGoogleStatusBadge(googleState, { statusLoading = false, fallbackLabel = "Enviado" } = {}) {
	if (!googleState) {
		if (statusLoading) {
			return { label: "Consultando...", className: "google-pending" };
		}
		return { label: fallbackLabel, className: "sent" };
	}

	switch (googleState.status) {
		case "active":
			return { label: "Ativo no Google", className: "google-active" };
		case "limited":
			return {
				label: "Limitado",
				className: "google-limited",
				title: googleState.issues?.[0]?.description,
			};
		case "pending":
		case "unknown":
			return { label: "Em análise", className: "google-pending" };
		case "error":
			return {
				label: "Com erro",
				className: "google-error",
				title: googleState.issues?.[0]?.description,
			};
		case "not_in_google":
			return { label: "Aguardando Google", className: "google-waiting" };
		default:
			return { label: fallbackLabel, className: "sent" };
	}
}

function getProductBadge(productId, { selected, isPending, googleStatus, statusLoading }) {
	if (isPending) {
		return { label: "Alteração pendente", className: "pending" };
	}

	if (!selected) {
		return { label: "Desconectado", className: "disconnected" };
	}

	return getGoogleStatusBadge(googleStatus?.productStatuses?.[String(productId)], { statusLoading });
}

function matchesGoogleFilter(filter, productId, selected, googleStatus) {
	if (!filter.startsWith("google_")) return true;

	const id = String(productId);
	if (!selected) return false;

	const status = googleStatus?.productStatuses?.[id]?.status;

	switch (filter) {
		case "google_active":
			return status === "active";
		case "google_limited":
			return status === "limited";
		case "google_pending":
			return status === "pending" || status === "unknown" || status === "not_in_google";
		case "google_error":
			return status === "error";
		case "google_issue":
			return status === "error" || status === "not_in_google";
		default:
			return true;
	}
}

export default function MerchantCenter({
	admin,
	products,
	saveProducts,
	fetchGoogleStatus,
	removeGoogleProducts,
	refreshList,
	listLoading,
}) {
	const [productsSelected, setProductsSelected] = useState([]);
	const [hasChanges, setHasChanges] = useState(false);
	const [notification, setNotification] = useState("");
	const [saving, setSaving] = useState(false);
	const [viewMode, setViewMode] = useState(loadViewMode);
	const [filter, setFilter] = useState("all");
	const [search, setSearch] = useState("");
	const [pageOffset, setPageOffset] = useState(0);
	const [googleStatus, setGoogleStatus] = useState(null);
	const [statusLoading, setStatusLoading] = useState(false);
	const [removingOfferId, setRemovingOfferId] = useState(null);

	const allDocs = products?.docs || [];

	const savedIds = useMemo(
		() => new Set(allDocs.filter((p) => p.addedMerchantCenter).map((p) => String(p._id))),
		[allDocs]
	);

	const refreshGoogleStatus = useCallback(async () => {
		if (!admin?.loja || !fetchGoogleStatus) return null;
		setStatusLoading(true);
		try {
			const data = await fetchGoogleStatus(admin.loja);
			if (data?.ok) {
				setGoogleStatus(data);
				return data;
			}
			return null;
		} catch {
			notify("Não foi possível consultar o status no Google.", setNotification, "error");
			return null;
		} finally {
			setStatusLoading(false);
		}
	}, [admin?.loja, fetchGoogleStatus]);

	useEffect(() => {
		if (!allDocs.length || hasChanges) return;
		setProductsSelected(allDocs.filter((p) => p.addedMerchantCenter).map((p) => p._id));
	}, [allDocs, hasChanges]);

	useEffect(() => {
		if (!admin?.loja) return;
		refreshGoogleStatus();
	}, [admin?.loja, refreshGoogleStatus]);

	useEffect(() => {
		setPageOffset(0);
	}, [filter, search, viewMode]);

	function setViewModeAndStore(mode) {
		setViewMode(mode);
		try {
			localStorage.setItem(VIEW_STORAGE_KEY, mode);
		} catch (_) {}
	}

	function isSelected(productId) {
		return productsSelected.some((id) => String(id) === String(productId));
	}

	function onChangeProduct(selected, productId) {
		const id = String(productId);
		const next = selected
			? productsSelected.filter((item) => String(item) !== id)
			: [...productsSelected, productId];
		setProductsSelected(next);
		setHasChanges(true);
	}

	function selectAllVisible(visibleDocs) {
		const ids = visibleDocs.map((p) => p._id);
		const merged = new Set([...productsSelected.map(String), ...ids.map(String)]);
		setProductsSelected([...merged]);
		setHasChanges(true);
	}

	function deselectAllVisible(visibleDocs) {
		const visible = new Set(visibleDocs.map((p) => String(p._id)));
		setProductsSelected(productsSelected.filter((id) => !visible.has(String(id))));
		setHasChanges(true);
	}

	async function handleRefresh() {
		await refreshList();
		await refreshGoogleStatus();
	}

	async function handleRemoveFromGoogle(item) {
		const label = item.title || item.offerId;
		if (!window.confirm(`Remover "${label}" do Google Merchant Center?`)) return;

		setRemovingOfferId(item.offerId);
		try {
			const result = await removeGoogleProducts([item.offerId], admin.loja);
			if (result?.ok === false) {
				notify(result.error || "Falha ao remover produto do Google.", setNotification, "error");
			} else {
				notify("Produto removido do Google.", setNotification);
				await refreshGoogleStatus();
			}
		} catch {
			notify("Erro ao remover produto do Google.", setNotification, "error");
		} finally {
			setRemovingOfferId(null);
		}
	}

	async function handleSave() {
		setSaving(true);
		try {
			const result = await saveProducts(productsSelected, admin.loja);
			if (result?.ok === false) {
				const failed = result.failed?.length || 0;
				notify(
					failed
						? `Sincronização parcial: ${failed} produto(s) falharam.`
						: result.error || "Falha ao sincronizar produtos.",
					setNotification,
					"error",
					6000
				);
			} else {
				notify("Produtos sincronizados com sucesso.", setNotification);
				setHasChanges(false);
				await refreshList();
				await refreshGoogleStatus();
			}
		} catch {
			notify("Erro ao sincronizar produtos.", setNotification, "error");
		} finally {
			setSaving(false);
		}
	}

	const filteredDocs = useMemo(() => {
		const term = search.trim().toLowerCase();
		const selectedSet = new Set(productsSelected.map(String));
		return allDocs.filter((item) => {
			const id = String(item._id);
			if (filter === "connected" && !selectedSet.has(id)) return false;
			if (filter === "disconnected" && selectedSet.has(id)) return false;
			if (!matchesGoogleFilter(filter, id, selectedSet.has(id), googleStatus)) return false;
			if (term && !String(item.titulo || "").toLowerCase().includes(term)) return false;
			return true;
		});
	}, [allDocs, filter, search, productsSelected, googleStatus]);

	const pageDocs = filteredDocs.slice(pageOffset, pageOffset + PAGE_SIZE);
	const sentCount = googleStatus?.summary?.sentCount ?? savedIds.size;
	const googleTotal = googleStatus?.summary?.googleTotal;
	const activeCount = googleStatus?.summary?.activeCount ?? 0;
	const limitedCount = googleStatus?.summary?.limitedCount ?? 0;
	const waitingCount = googleStatus?.summary?.notInGoogleCount ?? 0;
	const errorCount = googleStatus?.summary?.errorCount ?? 0;
	const pendingReviewCount = googleStatus?.summary?.pendingCount ?? 0;
	const totalCount = products?.totalDocs ?? allDocs.length;
	const pendingCount = countPending(productsSelected, savedIds);
	const extraInGoogleCount = googleStatus?.summary?.extraInGoogleCount ?? 0;
	const refreshing = listLoading || statusLoading;

	const googleOnlyProducts = useMemo(() => {
		const items = googleStatus?.googleOnlyProducts || [];
		const term = search.trim().toLowerCase();
		return items.filter((item) => {
			if (term && !String(item.title || "").toLowerCase().includes(term)) return false;
			if (filter === "connected" || filter === "disconnected") return false;
			if (filter === "google_active") return item.status === "active";
			if (filter === "google_limited") return item.status === "limited";
			if (filter === "google_pending") {
				return item.status === "pending" || item.status === "unknown";
			}
			if (filter === "google_error" || filter === "google_issue") return item.status === "error";
			return true;
		});
	}, [googleStatus, search, filter]);

	const initialLoading = listLoading && !allDocs.length;

	if (initialLoading) return <Loading />;

	function renderBadge(item, selected, isPending) {
		const badge = getProductBadge(item._id, {
			selected,
			isPending,
			googleStatus,
			statusLoading,
		});

		return (
			<span className={`badge ${badge.className}`} title={badge.title || undefined}>
				{badge.label}
			</span>
		);
	}

	function renderGoogleOnlyBadge(item) {
		const badge = getGoogleStatusBadge(item, { fallbackLabel: "No Google" });
		return (
			<span className={`badge ${badge.className}`} title={badge.title || undefined}>
				{badge.label}
			</span>
		);
	}

	return (
		<div className="MerchantCenter">
			<div className="head">
				<div className="text">
					<h1>Produtos no Merchant Center</h1>
					<p className="merchant-stats">
						<strong>{sentCount}</strong> enviados ·{" "}
						{googleTotal != null ? (
							<>
								<strong>{googleTotal}</strong> processados pelo Google
							</>
						) : (
							"consultando Google..."
						)}
						{activeCount > 0 && (
							<span className="merchant-active"> · {activeCount} ativos</span>
						)}
						{limitedCount > 0 && (
							<span className="merchant-limited"> · {limitedCount} limitados</span>
						)}
						{waitingCount > 0 && (
							<span className="merchant-waiting"> · {waitingCount} aguardando Google</span>
						)}
						{pendingReviewCount > 0 && (
							<span className="merchant-review"> · {pendingReviewCount} em análise</span>
						)}
						{errorCount > 0 && (
							<span className="merchant-error-count"> · {errorCount} com erro</span>
						)}
						{extraInGoogleCount > 0 && (
							<span className="merchant-extra"> · {extraInGoogleCount} só no Google</span>
						)}
						{pendingCount > 0 && (
							<span className="merchant-pending"> · {pendingCount} alteração(ões) pendente(s)</span>
						)}
					</p>
					<p className="merchant-stats-secondary">
						<strong>{productsSelected.length}</strong> selecionados · <strong>{totalCount}</strong> no catálogo
					</p>
					<p className="merchant-hint">
						“Enviado” significa que a API aceitou o produto. O Google pode levar alguns minutos para
						processar e exibir no Merchant Center.
						{googleStatus?.syncedAt && (
							<span className="merchant-sync-time">
								{" "}
								Status Google atualizado às{" "}
								{new Date(googleStatus.syncedAt).toLocaleTimeString("pt-BR", {
									hour: "2-digit",
									minute: "2-digit",
								})}
								.
							</span>
						)}
					</p>
				</div>
				<div className="actions">
					<button
						type="button"
						className="btn-secondary"
						disabled={refreshing || saving}
						onClick={handleRefresh}
					>
						<i className={`fas fa-sync-alt${refreshing ? " fa-spin" : ""}`} /> Atualizar
					</button>
					<button
						type="button"
						onClick={() =>
							window.open(
								`https://merchants.google.com/mc/items?a=${merchantCenterAccountId}&hl=pt&pli=1`
							)
						}
					>
						Ir para o Merchant Center <i className="fas fa-external-link-alt" />
					</button>
				</div>
			</div>

			<Notification notification={notification} />

			<div className="merchant-toolbar">
				<div className="merchant-view-toggle">
					<button
						type="button"
						className={viewMode === "grid" ? "active" : ""}
						onClick={() => setViewModeAndStore("grid")}
						title="Grade"
					>
						<i className="fas fa-th" />
					</button>
					<button
						type="button"
						className={viewMode === "list" ? "active" : ""}
						onClick={() => setViewModeAndStore("list")}
						title="Lista"
					>
						<i className="fas fa-list" />
					</button>
				</div>

				<input
					type="search"
					className="merchant-search"
					placeholder="Buscar produto..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>

				<select
					className="merchant-filter"
					value={filter}
					onChange={(e) => setFilter(e.target.value)}
				>
					<option value="all">Todos</option>
					<option value="connected">Enviados</option>
					<option value="disconnected">Não conectados</option>
					<option value="google_active">Ativos no Google</option>
					<option value="google_limited">Limitados no Google</option>
					<option value="google_pending">Em análise / aguardando</option>
					<option value="google_error">Com erro no Google</option>
					<option value="google_issue">Precisam de atenção</option>
				</select>

				<div className="merchant-bulk">
					<button type="button" onClick={() => selectAllVisible(pageDocs)}>
						Marcar página
					</button>
					<button type="button" onClick={() => deselectAllVisible(pageDocs)}>
						Desmarcar página
					</button>
				</div>
			</div>

			{refreshing && allDocs.length > 0 && (
				<p className="merchant-loading-inline">
					<i className="fas fa-spinner fa-spin" /> Atualizando dados...
				</p>
			)}

			<div className={`products${viewMode === "list" ? " list-view" : ""}`}>
				{pageDocs.length === 0 ? (
					<p className="merchant-empty">Nenhum produto encontrado.</p>
				) : (
					pageDocs.map((item) => {
						const selected = isSelected(item._id);
						const wasSaved = savedIds.has(String(item._id));
						const isPending = hasChanges && selected !== wasSaved;

						if (viewMode === "list") {
							return (
								<div
									key={item._id}
									className={`product product-row${selected ? " selected" : ""}${isPending ? " pending" : ""}`}
									onClick={() => onChangeProduct(selected, item._id)}
								>
									<div className={`checkbox row-check ${selected ? "active" : ""}`}>
										<i className="fas fa-check" />
									</div>
									<div
										className="picture thumb"
										style={{
											backgroundImage: `url(${apiLinkImages}/public/images/${item.fotos?.[0] || ""})`,
										}}
									/>
									<div className="details row-details">
										<h4 title={item.titulo}>{item.titulo}</h4>
									</div>
									<div className="price-col">
										<span className="price">{formatMoney(item.preco)}</span>
									</div>
									<div className="status-col">
										{renderBadge(item, selected, isPending)}
									</div>
								</div>
							);
						}

						return (
							<div
								key={item._id}
								className={`product${selected ? " selected" : ""}${isPending ? " pending" : ""}`}
								onClick={() => onChangeProduct(selected, item._id)}
							>
								<div className={`checkbox ${selected ? "active" : ""}`}>
									<i className="fas fa-check" />
								</div>
								{item.fotos?.[0] && (
									<div
										className="picture"
										style={{
											backgroundImage: `url(${apiLinkImages}/public/images/${item.fotos[0]})`,
										}}
									/>
								)}
								<div className="details">
									<h4>{item.titulo}</h4>
									<span className="price">{formatMoney(item.preco)}</span>
									{renderBadge(item, selected, isPending)}
								</div>
							</div>
						);
					})
				)}
			</div>

			{filteredDocs.length > PAGE_SIZE && (
				<Paginacao
					total={filteredDocs.length}
					atual={pageOffset}
					limite={PAGE_SIZE}
					onClick={setPageOffset}
				/>
			)}

			{googleOnlyProducts.length > 0 && (
				<div className="merchant-google-only">
					<h3>Produtos no Google sem vínculo no app ({googleOnlyProducts.length})</h3>
					<p className="merchant-hint">
						Itens processados pelo Google que não estão marcados como enviados no catálogo local
						(ex.: produto de teste ou itens da fonte antiga). Use &quot;Remover do Google&quot; para
						excluí-los do Merchant Center.
					</p>
					<div className="products list-view">
						{googleOnlyProducts.map((item) => (
							<div key={item.offerId} className="product product-row google-only-row">
								<div className="picture thumb google-only-thumb">
									<i className="fab fa-google" />
								</div>
								<div className="details row-details">
									<h4 title={item.title}>{item.title}</h4>
									<span className="google-only-id">ID: {item.offerId}</span>
								</div>
								<div className="status-col">{renderGoogleOnlyBadge(item)}</div>
								<div className="google-only-actions">
									<button
										type="button"
										className="btn-remove-google"
										disabled={removingOfferId === item.offerId}
										onClick={() => handleRemoveFromGoogle(item)}
									>
										{removingOfferId === item.offerId ? (
											<i className="fas fa-spinner fa-spin" />
										) : (
											"Remover do Google"
										)}
									</button>
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{hasChanges && (
				<button
					type="button"
					className="button-float"
					disabled={saving}
					onClick={handleSave}
				>
					{saving ? (
						<>
							<i className="fas fa-spinner fa-spin" /> Salvando...
						</>
					) : (
						`Salvar${pendingCount ? ` (${pendingCount})` : ""}`
					)}
				</button>
			)}
		</div>
	);
}
